import api from '../../services/api';
import { ApiErrorResponse, CreateEstatePayload, CreateEstateResponse, EditEstatePayload, EditEstateResponse, Estate, ExecutorInvitePayload, ExecutorInviteResponse } from '../../types';
import Cookies from 'js-cookie';

/**
 * Type guard to check if the error is an AxiosError.
 */
function isAxiosError(error: unknown): error is { response: { data: { message: string } } } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

/**
 * Search estates based on a search query.
 * @param query - The search term.
 * @returns An array of estates matching the search criteria.
 */
export const searchEstates = async (query: string): Promise<Estate[]> => {
  try {
    const response = await api.get(`/estates/search?q=${encodeURIComponent(query)}`);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to search estates');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Fetch all estates.
 * @returns An array of all estates.
 */
export const fetchEstates = async (): Promise<Estate[]> => {
  try {
    const response = await api.get('/estates');
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to fetch estates');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Create a new estate.
 * @param estateData - The data for creating a new estate.
 * @param token - The authentication token.
 * @returns The response from the API.
 */
export const createEstate = async (
  estateData: CreateEstatePayload,
  token: string
): Promise<CreateEstateResponse> => {
  try {
    const response = await api.post('/estates', estateData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to create estate');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Fetch details of a specific estate.
 * @param id - The ID of the estate.
 * @param token - The authentication token.
 * @returns The estate details.
 */
export const fetchEstateDetails = async (
  id: string,
  token: string
): Promise<Estate> => {
  try {
    const response = await api.get(`/estates/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to fetch estate details');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Edit an existing estate.
 * @param uuid - The UUID of the estate to edit.
 * @param estateData - The updated estate data.
 * @param token - The authentication token.
 * @returns The response from the API.
 */
export const editEstate = async (
  uuid: string,
  estateData: EditEstatePayload,
  token: string
): Promise<EditEstateResponse> => {
  try {
    const response = await api.put(`/estates/${uuid}`, estateData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to edit estate');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Invite an executor to an estate.
 * @param estateId - The ID of the estate.
 * @param executorData - The data for inviting the executor.
 * @param token - The authentication token.
 * @returns The response from the API.
 */
export const inviteExecutor = async (
  estateId: string,
  executorData: ExecutorInvitePayload,
  token: string
): Promise<ExecutorInviteResponse> => {
  try {
    const response = await api.post(`/estates/${estateId}/executors/invite`, executorData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to invite executor');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Delete an estate by estateId.
 * @param estateId - The ID of the estate to delete.
 * @param token - The authentication token.
 * @returns The response from the API.
 */
export const deleteEstate = async (estateId: string, token: string): Promise<void> => {
  try {
    await api.delete(`/estates/${estateId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to delete estate');
    }
    throw new Error('An unexpected error occurred');
  }
};


/**
 * Add Executor to Participants.
 * @param estateId - The ID of the estate to delete.
 * @param executorUid - The Executor UID.
 * @returns The response from the API.
 */
export const addExecutorToParticipants = async (estateId: string, executorUid: string): Promise<void> => {
  try {
    await api.get(`/estates/${estateId}/executors/${executorUid}/participant`);
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to add executor to participants');
    }
    throw new Error('An unexpected error occurred');
  }
}